import { AddressLink } from 'Modules'
import { BaseError, decodeErrorResult, Hex, parseAbi } from 'viem'

import { customErrorContract } from 'hooks/contract/use-contract-viem'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

/** BE-prepared tx: target contract + ABI-encoded calldata */
export interface PreparedTx {
  to: string
  data: string
  /** wei as decimal string; omitted = 0 */
  value?: string
}

/**
 * Reverts the BE cannot pre-check since they depend on when the admin signs or
 * which wallet is connected. Errors-only ABI - just enough to decode them.
 */
const KNOWN_ERRORS_ABI = parseAbi([
  'error PTOGovernor_InvalidSnapshot(uint48 snapshot)',
  'error AgentRole_UnauthorizedAgent(address account)',
  'error AssetStateMachine_InvalidTransition(address pto, uint8 from, uint8 to)',
  'error RedemptionPoolFactory_PoolAlreadyExisted()',
  'error Pool_InvalidTime()',
])

const KNOWN_ERROR_MESSAGES: Record<string, string> = {
  PTOGovernor_InvalidSnapshot:
    'Voting start time has already passed. Please pick a later start time and try again.',
  AgentRole_UnauthorizedAgent:
    'The connected wallet is not authorized for this action. Please connect the admin wallet.',
  AssetStateMachine_InvalidTransition:
    'The asset is not in the stage this action needs. Please reload the page and check its status.',
  RedemptionPoolFactory_PoolAlreadyExisted:
    'This PTO already has a pool on-chain. Each PTO can only have one pool.',
  Pool_InvalidTime: 'Claim open time has already passed. Please pick a later time and try again.',
}

/** revert data sits on the deepest cause, as `data` or `data.data` depending on the RPC */
const getRevertData = (error: BaseError): Hex | undefined => {
  const cause: any = error.walk()
  const data = typeof cause?.data === 'object' ? cause.data?.data : cause?.data
  return typeof data === 'string' && data.startsWith('0x') ? (data as Hex) : undefined
}

/** Message for a failed DAO tx - prepared (no ABI) or ABI-based */
export const daoTxErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof BaseError) {
    const data = getRevertData(error)
    if (data) {
      try {
        const { errorName } = decodeErrorResult({ abi: KNOWN_ERRORS_ABI, data })
        if (KNOWN_ERROR_MESSAGES[errorName]) return KNOWN_ERROR_MESSAGES[errorName]
      } catch {
        // not one of the known errors
      }
    }
  }
  return customErrorContract(error) || (error instanceof BaseError ? error.shortMessage : undefined)
}

/**
 * Sends a BE-prepared tx as is from the manager wallet - no ABI needed
 * (DAO_GOVERNANCE_ERC3643_FE_NOTE 1). eth_call first so a revert surfaces
 * before the wallet prompt, then send and wait for the receipt.
 */
export const usePreparedTx = () => {
  const sendPreparedTx = async (address: AddressLink, tx: PreparedTx) => {
    if (!address) throw new Error('Wallet is not connected')
    if (!tx?.to || !tx?.data) throw new Error('On-chain call data is missing')

    const account = address as `0x${string}`
    const to = tx.to as `0x${string}`
    const data = tx.data as `0x${string}`
    const value = tx.value ? BigInt(tx.value) : undefined

    try {
      // batch: false - a multicall would run it as Multicall3 instead of the admin wallet
      await publicWallet.call({ account, to, data, value, batch: false })
      const txHash = await clientWallet.sendTransaction({ account, to, data, value })

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 6,
      })
      if (receipt.status === 'reverted') throw new Error('Transaction reverted on-chain')

      return { txHash, receipt }
    } catch (error) {
      logError('error prepared tx on-chain-----------------', error)
      throw error
    }
  }

  return { sendPreparedTx }
}
